<template lang="pug">
.trees-chart(v-if="currentRating && newRating")
  TreeSlider(:trees-count="treeCount" :berRating="currentRating" :sliderLabel="'Current'")
  TreeSlider(:trees-count="treeCount" :berRating="newRating" :sliderLabel="'New'" :label-position="'bottom'")
  .rating-colors
    .color(v-for="rating in ratingDisplayInfos" :style="{ backgroundColor: rating.color, width: `${rating.size}%`, color: pickTextOn(rating.color)}")
  .rating-labels(v-if="showAllLabels")
    .rating-label(v-for="rating in ratingDisplayInfos" :style="{ width: `${rating.size}%`}")
      .label {{ rating.code }}
  .rating-labels(v-else)
    .rating-label.rating-grouped(v-for="rating in groupedRatingDisplayInfos" :style="{ width: `calc(${rating.size}% - 3px`, borderColor: rating.color}")
      .label {{ rating.code }}
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";
import TreeSlider from "./TreeSlider.vue";
import { BerRatings, calculatePercentage } from "@/helpers";
import type { Rating } from "@/types";
import {pickTextOn} from "@/helpers/font-contrast";

export default defineComponent({
  components: { TreeSlider },
  props: {
    treeCount: {
      type: Number,
      required: true,
    },
    currentRating: {
      type: Object as PropType<Rating>,
      required: true,
    },
    newRating: {
      type: Object as PropType<Rating>,
      required: true,
    },
  },
  setup() {
    const showAllLabels = ref(window.innerWidth > 720);
    window.addEventListener(
      "resize",
      () => (showAllLabels.value = window.innerWidth > 720)
    );
    const ratingDisplayInfos = Array.from([...BerRatings.entries()]).map(
      ([key, item]) => {
        const widthPercentage = calculatePercentage(item.level);

        return {
          code: key,
          color: item.color,
          size: widthPercentage,
        };
      }
    );
    const groupedRatingDisplayInfos = Object.values(
      Array.from([...BerRatings.entries()]).reduce((acc, [key, item]) => {
        const groupKey = getGroupedRatingCode(key);
        const size = calculatePercentage(item.level);

        if (!acc[groupKey]) {
          acc[groupKey] = {
            code: groupKey,
            color: item.color,
            size: 0,
          };
        }

        acc[groupKey].size += size;
        return acc;
      }, {} as Record<string, { code: string; color: string; size: number }>)
    );

    function getGroupedRatingCode(code: string) {
      return code.toUpperCase() === "A0" ? "A0" : code[0].toUpperCase();
    }

    return {
      ratingDisplayInfos,
      groupedRatingDisplayInfos,
      showAllLabels,
      pickTextOn,
    };
  },
});
</script>

<style scoped lang="scss">
.trees-chart {
  background-color: #f0f0f0;
  padding: 5rem 0 0 0;
  margin-top: 1rem;

  .rating-colors {
    display: flex;
    height: 0.75rem;
    margin-top: 0.75rem;
  }

  .rating-labels {
    display: flex;
    padding-top: 0.25rem;
    text-align: center;
    background-color: $background-color-default;
    color: $primary-color-hover;
    font-size: 0.9rem;
    padding-top: 0.5rem;

    .label {
      padding-top: 0.4rem;
    }

    .rating-grouped {
      border-top: 2px solid $primary-color-hover;
      margin: auto 3px;

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }

      & .label {
        border-top: 1px solid $font-color-secondary;
      }
    }
  }
}
</style>
